import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertTriangle,
  AtSign,
  Award,
  Bell,
  Building2,
  CalendarClock,
  CheckCheck,
  Clock,
  Eye,
  Flag,
  Gift,
  HardDrive,
  Layers,
  LifeBuoy,
  ListChecks,
  ListPlus,
  Megaphone,
  MessageSquare,
  RefreshCw,
  Search,
  Shield,
  Sparkles,
  Trash2,
  TrendingUp,
  User as UserIcon,
  UserMinus,
  UserPlus,
  Target,
  X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import { api, extractApiError } from '../lib/api'
import { notificationsResponse } from '../lib/types'
import { formatDateTime } from '../lib/format'
import { Button } from '../components/ui/Button'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { Pagination } from '../components/ui/Pagination'
import { SpaceInviteActions } from '../components/SpaceInviteActions'
import { EmptyStateBanner } from '../components/EmptyStateBanner'
import { usePageSize } from '../lib/display-prefs'

// This list's own page sizes (the notifications API clamps to 100).
const NOTIFICATION_PAGE_SIZES = [10, 25, 50, 100]

/**
 * Shared Notifications page — mounted on every panel
 * (/super-admin/notifications, /notifications, /app/notifications).
 *
 * Mirrors the ProcessPro Notifications surface (full-page triage of
 * everything that's hit the bell) with three deliberate departures
 * from ProcessPro's current implementation:
 *
 *  1. Type filter is pushed to the SERVER, so pagination totals and
 *     "next page" hit-tests stay correct when the user narrows the
 *     view (ProcessPro filters client-side, which leaves you on
 *     paginated pages full of hidden checklist rows).
 *  2. Every unread row carries an Eye / Mark-as-read icon. An earlier
 *     revision hid it on linked rows ("row click already marks read"),
 *     but that left no way to mark a notification read WITHOUT
 *     navigating away — QA flagged it; the icon is back for all
 *     unread rows and stops propagation so it never navigates.
 *  3. Rows ALWAYS render `role="button"` + `tabIndex={0}` so
 *     keyboard / screen-reader users can act on a row whether or not
 *     it has a deep link.
 */

// Type config — icon + accent class per notification kind. Falls back
// to the generic Bell + slate when a kind isn't enumerated, so adding
// new kinds in the backend never breaks the page; it just renders the
// generic treatment until someone adds an entry here.
const TYPE_CONFIG = {
  announcement: { icon: Megaphone, tone: 'accent', label: 'Announcement' },
  'announcement.high': {
    icon: Megaphone,
    tone: 'warning',
    label: 'Priority Announcement',
  },
  'task.created': { icon: ListPlus, tone: 'info', label: 'Task created' },
  'task.assigned': { icon: UserPlus, tone: 'info', label: 'Task assigned' },
  'task.unassigned': {
    icon: UserMinus,
    tone: 'slate',
    label: 'Task unassigned',
  },
  'task.status_changed': {
    icon: Bell,
    tone: 'info',
    label: 'Status changed',
  },
  'task.priority_changed': {
    icon: Flag,
    tone: 'accent',
    label: 'Priority changed',
  },
  'task.due_date_changed': {
    icon: CalendarClock,
    tone: 'info',
    label: 'Due date changed',
  },
  'comment.added': {
    icon: MessageSquare,
    tone: 'info',
    label: 'Comment',
  },
  'comment.mention': {
    icon: AtSign,
    tone: 'accent',
    label: 'Mention',
  },
  'badge.awarded': {
    icon: Award,
    tone: 'accent',
    label: 'Badge earned',
  },
  'list.deleted': { icon: Trash2, tone: 'danger', label: 'List deleted' },
  'space.deleted': { icon: Trash2, tone: 'danger', label: 'Space deleted' },
  'workspace.added': {
    icon: UserPlus,
    tone: 'info',
    label: 'Workspace added',
    // Retired — the backend no longer emits this kind; the entry stays
    // so HISTORICAL rows keep their chip, but the filter dropdown skips
    // it (offering a filter that can never match new data read as a
    // bug to QA).
    retired: true,
  },
  'workspace.removed': {
    icon: UserMinus,
    tone: 'danger',
    label: 'Workspace removed',
  },
  'workspace.access_changed': {
    icon: Shield,
    tone: 'info',
    label: 'Workspace access changed',
  },
  'access.level_changed': {
    icon: Shield,
    tone: 'info',
    label: 'Space access changed',
  },
  'workspace.ownership_transferred': {
    icon: Award,
    tone: 'accent',
    label: 'Ownership transferred',
  },
  'workspace.department_changed': {
    icon: Building2,
    tone: 'info',
    label: 'Department updated',
    // Retired — the in-app department-change notification was removed
    // by request (workspaces.service.js documents it); chip kept for
    // historical rows, dropdown skips it.
    retired: true,
  },
  'user.account_updated': {
    icon: UserIcon,
    tone: 'slate',
    label: 'Account updated',
  },
  // ── Kinds that previously fell through to the generic fallback ──
  // QA flagged that every support-ticket row wore the same static
  // "NOTIFICATION" chip; this block enumerates every remaining kind
  // the backend actually emits (grep `kind: '…'` across the API) so
  // each row's chip — and the type-filter dropdown built from this
  // map — says what the notification IS.
  'support.created': { icon: LifeBuoy, tone: 'info', label: 'New ticket' },
  'support.reply': {
    icon: MessageSquare,
    tone: 'accent',
    label: 'Ticket reply',
  },
  'support.status': {
    icon: RefreshCw,
    tone: 'info',
    label: 'Ticket status',
  },
  'task.completed': {
    icon: CheckCheck,
    tone: 'success',
    label: 'Task completed',
  },
  'task.due_soon': { icon: Clock, tone: 'warning', label: 'Due soon' },
  'task.overdue': {
    icon: AlertTriangle,
    tone: 'danger',
    label: 'Task overdue',
  },
  'goal.assigned': {
    icon: Target,
    tone: 'accent',
    label: 'Goal assigned',
  },
  'goal.follower_added': {
    icon: UserIcon,
    tone: 'info',
    label: 'Goal follow',
  },
  'goal.checkin': {
    icon: MessageSquare,
    tone: 'accent',
    label: 'Goal progress update',
  },
  'goal.progress_changed': {
    icon: TrendingUp,
    tone: 'info',
    label: 'Key result updated',
  },
  'goal.reminder': {
    icon: Clock,
    tone: 'warning',
    label: 'Goal reminder',
  },
  'goal.completed': {
    icon: Award,
    tone: 'success',
    label: 'Goal completed',
  },
  'goal.status_changed': {
    icon: Flag,
    tone: 'info',
    label: 'Goal status changed',
  },
  'goal.owner_changed': {
    icon: RefreshCw,
    tone: 'accent',
    label: 'Goal owner changed',
  },
  'goal.target_date_changed': {
    icon: CalendarClock,
    tone: 'info',
    label: 'Goal dates changed',
  },
  'goal.weekly_reminder': {
    icon: Clock,
    tone: 'warning',
    label: 'Goal reminder',
  },
  'goal.subgoal_added': {
    icon: ListPlus,
    tone: 'accent',
    label: 'Sub-goal added',
  },
  'limit.ai_usage_warning': {
    icon: Sparkles,
    tone: 'warning',
    label: 'AI usage warning',
  },
  'limit.storage_warning': {
    icon: HardDrive,
    tone: 'warning',
    label: 'Storage warning',
  },
  'referral.rewarded': { icon: Gift, tone: 'accent', label: 'Referral reward' },
  'space.invited': { icon: Layers, tone: 'info', label: 'Space invite' },
  'list.invited': { icon: ListChecks, tone: 'info', label: 'List invite' },
  'space.invitation.accepted': {
    icon: CheckCheck,
    tone: 'success',
    label: 'Space invite accepted',
  },
  'space.invitation.declined': {
    icon: X,
    tone: 'slate',
    label: 'Space invite declined',
  },
  'list.invitation.accepted': {
    icon: CheckCheck,
    tone: 'success',
    label: 'List invite accepted',
  },
  'list.invitation.declined': {
    icon: X,
    tone: 'slate',
    label: 'List invite declined',
  },
  'space.removed': {
    icon: UserMinus,
    tone: 'danger',
    label: 'Space access removed',
  },
}

const TONE_CLASS = {
  info: 'bg-[color:var(--color-info-bg)] text-[color:var(--color-info)] border-[color:var(--color-info-border)]',
  success:
    'bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border-[color:var(--color-success-border)]',
  warning:
    'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]',
  danger:
    'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border-[color:var(--color-danger-border)]',
  accent:
    'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]',
  // The `slate` tone backs neutral badges like "Task unassigned" and the
  // generic "Notification" fallback. Its light-mode fill is the page
  // canvas, which in DARK mode collapses to dark-text-on-dark-background
  // (poor contrast). Add an explicit dark-mode chip — a translucent light
  // fill + light text + hairline border — matching the dark treatment the
  // danger count badge already uses, so these badges stay readable in dark
  // mode (DF_003 / bug 16604).
  slate:
    'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)] dark:bg-white/[0.08] dark:text-slate-200 dark:border-white/10',
}

// Exported so the header NotificationsBell renders the SAME icon per kind as
// this full page — previously the bell had its own 4-prefix map and showed a
// bare Bell for most kinds (inconsistent icons for the same notification).
export function getTypeConfig(kind) {
  return (
    TYPE_CONFIG[kind] ?? {
      icon: Bell,
      tone: 'slate',
      label: 'Notification',
    }
  )
}

/** Readable label for a kind not enumerated in TYPE_CONFIG — drives the
 *  type-filter dropdown so an unknown kind still reads as words
 *  ('space.invitation.accepted' → 'Space invitation accepted') rather
 *  than the generic "Notification". */
function humaniseKind(kind) {
  const s = String(kind ?? '').replace(/[._]+/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : String(kind)
}

/** Two-letter initials from a full name. Returns null for empty so the
 *  caller can fall back to a type icon. */
function initials(name) {
  if (!name) return null
  const parts = String(name).trim().split(/\s+/)
  if (parts.length === 0 || parts[0] === '') return null
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? null
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}


export function NotificationsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  // Tab: 'all' | 'unread'. Server honours `unread=true` so pagination
  // totals reflect the chosen tab.
  const [tab, setTab] = useState('all')
  // Type filter: '' = no filter; otherwise a specific kind. Server-side
  // narrowing keeps pagination consistent.
  const [kindFilter, setKindFilter] = useState('')
  // Free-text search + feed order — both server-side (QA asked for
  // parity with the Support Tickets / Announcements toolbars). Search
  // matches title OR body, case-insensitively.
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('newest')
  const [page, setPage] = useState(1)
  // Rows per page — the shared Pagination bar's selector drives this.
  // Starts at (and follows) the account rows-per-page (My Preferences). The
  // option list is capped at 100 because the notifications API clamps
  // `limit` to 100 server-side, so a larger preference starts at 100.
  const [limit, setLimit] = usePageSize({ options: NOTIFICATION_PAGE_SIZES, max: 100 })
  // Announcement viewer — mirrors the bell. Announcement rows have no
  // `link` to navigate to, so clicking one opens a modal showing the
  // full title/body/priority. Long bodies are truncated to two lines
  // in the row, so the modal is the only way to read them in full.
  const [viewAnnouncement, setViewAnnouncement] = useState(null)
  // Row pending delete confirmation — the trash icon stages the row
  // here and the ConfirmDialog below does the actual mutate, so a
  // stray click can't silently destroy a notification (deletes are
  // permanent for this user's feed; there is no undo/restore).
  const [confirmDelete, setConfirmDelete] = useState(null)
  // Multi-select for bulk delete (QA-requested) — same pattern as the
  // Files page: a Set of selected ids, an action bar while non-empty,
  // and a staged {ids, count} the bulk ConfirmDialog acts on.
  const [selectedIds, setSelectedIds] = useState(new Set())
  // "Select all" mode: every notification matching the current view is
  // selected (across all pages). Delete then clears the whole set server-side
  // in one call rather than per id — so there's no page-size ceiling.
  const [selectAllMode, setSelectAllMode] = useState(false)
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState(null)

  // Global Escape closes the announcement modal — the portal-rendered
  // dialog isn't always focused (clicking the backdrop steals focus
  // back to <body>), so a window-level listener is the reliable
  // dismissal path. Same approach the bell uses.
  useEffect(() => {
    if (!viewAnnouncement) return
    function onKey(e) {
      if (e.key === 'Escape') setViewAnnouncement(null)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [viewAnnouncement])

  const listQuery = useQuery({
    queryKey: [
      'notifications',
      'list',
      tab,
      kindFilter,
      search,
      sort,
      page,
      limit,
    ],
    queryFn: async () => {
      const params = { page, limit }
      if (tab === 'unread') params.unread = true
      if (kindFilter) params.kind = kindFilter
      if (search.trim()) params.search = search.trim()
      if (sort === 'oldest') params.sort = 'oldest'
      const res = await api.get('/notifications', { params })
      return notificationsResponse.parse(res.data)
    },
    keepPreviousData: true,
  })

  const markReadMutation = useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => {
      toast.success('All notifications marked as read.')
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/notifications/${id}`),
    onSuccess: () => {
      toast.success('Notification deleted successfully.')
      setConfirmDelete(null)
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const rows = listQuery.data?.data ?? []
  const unreadCount = listQuery.data?.unreadCount ?? 0

  // Type-filter options built from the kinds the user ACTUALLY has
  // (from the server) — plus the current selection so it stays visible
  // even if it just dropped out of the set. This means the dropdown
  // never offers a type that would render "No notifications match your
  // filter". RETIRED kinds are excluded even when the user still holds
  // historical rows of them: the backend no longer emits these, so
  // offering a filter that can never match NEW activity reads as a bug
  // (e.g. "Workspace added", "Department updated"). Their chips still
  // render in the list via TYPE_CONFIG; they're just not filterable.
  // Labelled via TYPE_CONFIG (humanised fallback), sorted A→Z.
  const typeFilterOptions = [
    ...new Set([
      ...(listQuery.data?.availableKinds ?? []),
      ...(kindFilter ? [kindFilter] : []),
    ]),
  ]
    .filter((kind) => !TYPE_CONFIG[kind]?.retired)
    .map((kind) => ({ kind, label: TYPE_CONFIG[kind]?.label ?? humaniseKind(kind) }))
    .sort((a, b) => a.label.localeCompare(b.label))

  // Reset selection when the MATCHING SET changes (tab / type / search) — a
  // different filter must not carry stale ticks or a stale "select all".
  // Pagination + sort don't change membership, so they no longer clear it:
  // that lets a "Select all" survive paging while you review before deleting.
  useEffect(() => {
    setSelectedIds(new Set())
    setSelectAllMode(false)
  }, [tab, kindFilter, search])

  function toggleSelect(id) {
    // Ticking/unticking a row drops out of "all matching" mode into an
    // explicit id selection (Gmail-style) — from here Delete acts on ids.
    setSelectAllMode(false)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // "Select all" selects EVERY notification matching the current view, across
  // ALL pages — not just the visible ones. Rather than enumerate ids (the list
  // endpoint caps a page at 100), it flips a mode: the count shows the real
  // total and Delete clears the whole set server-side via one DELETE request
  // (see deleteAllMutation) — no page-size ceiling, no per-id fan-out. Ticking
  // the visible rows too so their checkboxes read as selected.
  function enterSelectAll() {
    setSelectAllMode(true)
    setSelectedIds(new Set(rows.map((n) => n.id)))
  }

  // Partial delete — for an EXPLICIT id selection (a handful of ticked rows),
  // fan out one DELETE per row with allSettled so a single failure never masks
  // the rest. A full "Select all" uses deleteAllMutation (server-side) instead,
  // so there's no page-size ceiling.
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/notifications/${id}`)),
      )
      return { results }
    },
    onSuccess: ({ results }) => {
      const ok = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.length - ok
      if (ok > 0) {
        toast.success(
          `${ok} notification${ok === 1 ? '' : 's'} deleted.` +
            (failed > 0 ? ` ${failed} failed.` : ''),
        )
      } else {
        toast.error('Could not delete the selected notifications.')
      }
      setSelectedIds(new Set())
      setBulkDeleteTarget(null)
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // "Delete all matching" — one server-side call that clears EVERY Inbox row
  // matching the current tab / type / search, with no 100-row cap and no
  // per-id fan-out. Powers the "Select all" → Delete path.
  const deleteAllMutation = useMutation({
    mutationFn: async () => {
      const params = {}
      if (tab === 'unread') params.unread = true
      if (kindFilter) params.kind = kindFilter
      if (search.trim()) params.search = search.trim()
      const res = await api.delete('/notifications', { params })
      return res.data
    },
    onSuccess: (data) => {
      const n = data?.count ?? 0
      toast.success(`${n} notification${n === 1 ? '' : 's'} deleted.`)
      setSelectedIds(new Set())
      setSelectAllMode(false)
      setBulkDeleteTarget(null)
      void qc.invalidateQueries({ queryKey: ['notifications'] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })
  const pagination = listQuery.data?.pagination ?? {
    page: 1,
    limit,
    total: 0,
    hasMore: false,
  }

  function handleRowClick(n) {
    if (!n.readAt) markReadMutation.mutate(n.id)
    // Announcement rows have no link to navigate to and their bodies
    // are clamped to two lines in the row — open a modal so long
    // bodies are readable from the full-page surface (same UX the
    // bell exposes for the dropdown).
    if (n.kind?.startsWith('announcement')) {
      setViewAnnouncement(n)
      return
    }
    if (!n.link) return
    // Internal vs external — external opens in a new tab, internal
    // navigates within the SPA. Same heuristic ProcessPro uses.
    const target = n.link
    if (target.startsWith('http://') || target.startsWith('https://')) {
      window.open(target, '_blank', 'noopener')
      return
    }
    navigate(target)
  }

  function changeTab(next) {
    setTab(next)
    setPage(1)
  }

  function changeKind(next) {
    setKindFilter(next)
    setPage(1)
  }

  function changeSearch(next) {
    setSearch(next)
    setPage(1)
  }

  function changeSort(next) {
    setSort(next)
    setPage(1)
  }

  return (
    <div>
      {/* Header — heading + Mark-all-read action. */}
      {/* Stacks below `sm` so the action button is never pushed off the right edge on a phone. */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <Bell
              size={20}
              className="text-[color:var(--color-accent-strong)]"
            />
            Notifications
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            All your alerts in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 ? (
            <Button
              variant="secondary"
              onClick={() => markAllReadMutation.mutate()}
              loading={markAllReadMutation.isPending}
            >
              <CheckCheck size={14} />
              Mark all read ({unreadCount})
            </Button>
          ) : null}
        </div>
      </div>

      {/* Tabs + type filter. Tabs gate the read state server-side;
       *  the dropdown narrows by kind, also server-side. */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-1">
          <TabButton
            label="All"
            active={tab === 'all'}
            onClick={() => changeTab('all')}
          />
          <TabButton
            label={`Unread${unreadCount > 0 ? ` (${unreadCount})` : ''}`}
            active={tab === 'unread'}
            onClick={() => changeTab('unread')}
          />
        </div>
        {/* Free-text search — server-side title/body contains-match,
         *  same toolbar affordance the Support Tickets / Announcements
         *  pages carry (QA-requested parity). */}
        <div className="relative w-80 max-w-full">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 z-10 text-[color:var(--color-ink-muted)]"
          >
            <Search size={15} />
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => changeSearch(e.target.value)}
            // Capped to match the API’s `search` param (max 200).
            maxLength={200}
            placeholder="Search by title or message"
            aria-label="Search notifications"
            className="fd-search-pill h-9 w-full pl-9 pr-4 rounded-full text-sm outline-none placeholder:text-[color:var(--color-ink-subtle)]"
          />
        </div>
        {/* Feed order — a feed sorts by time, so the two meaningful
         *  orders are offered rather than a per-column sorter. */}
        <select
          value={sort}
          onChange={(e) => changeSort(e.target.value)}
          aria-label="Sort notifications"
          className="ml-auto h-9 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm cursor-pointer outline-none focus:border-[color:var(--color-accent)]"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
        </select>
        <select
          value={kindFilter}
          onChange={(e) => changeKind(e.target.value)}
          aria-label="Filter by notification type"
          className="h-9 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm cursor-pointer outline-none focus:border-[color:var(--color-accent)]"
        >
          <option value="">All types</option>
          {/* Built from the kinds the user actually has (server-provided
           *  availableKinds) so every option matches at least one row —
           *  no dead filters. Retired kinds are excluded (see
           *  typeFilterOptions) — the backend no longer emits them. */}
          {typeFilterOptions.map(({ kind, label }) => (
            <option key={kind} value={kind}>
              {label}
            </option>
          ))}
        </select>
        {/* Clear filters — shown once a type filter OR a search is
         *  active, so there's a one-click reset next to the controls
         *  (the filtered empty state's "Clear the filter…" copy has a
         *  visible target). Same conditional-clear pattern as
         *  WorkReportsPage / ReportsPage; resets the page to 1 via the
         *  change handlers. Sort is an order preference, not a filter,
         *  so it's left as-is. */}
        {kindFilter || search ? (
          <button
            type="button"
            onClick={() => {
              changeKind('')
              changeSearch('')
            }}
            title="Clear search and type filter"
            className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-[color:var(--color-rule)] text-xs font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:border-[color:var(--color-ink-muted)] cursor-pointer"
          >
            <X size={12} />
            Clear filters
          </button>
        ) : null}
      </div>

      {/* Bulk-selection bar — appears while any row is ticked or a full
       *  "Select all" is active. */}
      {selectedIds.size > 0 || selectAllMode ? (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-4 py-2.5">
          <span className="text-sm font-medium text-[color:var(--color-ink-strong)]">
            {selectAllMode ? pagination.total : selectedIds.size} selected
          </span>
          <Button
            size="sm"
            variant="secondary"
            onClick={enterSelectAll}
            disabled={selectAllMode || pagination.total === 0}
          >
            {`Select all${pagination.total ? ` (${pagination.total})` : ''}`}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setSelectedIds(new Set())
              setSelectAllMode(false)
            }}
          >
            <X size={14} /> Deselect All
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() =>
              setBulkDeleteTarget(
                selectAllMode
                  ? { all: true, count: pagination.total }
                  : { ids: Array.from(selectedIds), count: selectedIds.size },
              )
            }
          >
            <Trash2 size={14} /> Delete
          </Button>
        </div>
      ) : null}

      {/* List card. */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
        {listQuery.isLoading ? (
          <p className="p-8 text-center text-sm text-[color:var(--color-ink-muted)]">
            Loading notifications…
          </p>
        ) : listQuery.isError ? (
          <p className="p-8 text-center text-sm text-[color:var(--color-priority-urgent)]">
            {extractApiError(listQuery.error).message}
          </p>
        ) : rows.length === 0 ? (
          <EmptyState tab={tab} hasFilter={Boolean(kindFilter) || Boolean(search.trim())} />
        ) : (
          <ul className="divide-y divide-[color:var(--color-rule)]">
            {rows.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                selected={selectAllMode || selectedIds.has(n.id)}
                onToggleSelect={() => toggleSelect(n.id)}
                onClick={() => handleRowClick(n)}
                onDelete={() => setConfirmDelete(n)}
                onMarkRead={() => markReadMutation.mutate(n.id)}
                deleting={deleteMutation.isPending}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Announcement viewer modal — opened when an announcement.* row
       *  is clicked. Mirrors the bell's modal: backdrop click / X /
       *  Close button all dismiss; long bodies scroll inside the
       *  modal so the row's two-line clamp doesn't hide content. */}
      {viewAnnouncement
        ? createPortal(
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Announcement"
              className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setViewAnnouncement(null)
              }}
            >
              <div className="w-full max-w-lg rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[color:var(--color-rule)] flex items-start gap-3">
                  <span
                    className={
                      'mt-0.5 h-8 w-8 rounded-full flex items-center justify-center shrink-0 border ' +
                      (viewAnnouncement.kind === 'announcement.high'
                        ? TONE_CLASS.warning
                        : TONE_CLASS.accent)
                    }
                    aria-hidden="true"
                  >
                    <Megaphone size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {viewAnnouncement.kind === 'announcement.high' ? (
                        <span
                          className="inline-flex items-center shrink-0 rounded-full bg-[color:var(--color-danger)] text-white text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border border-transparent dark:border-white/10 dark:bg-white/[0.06] dark:text-rose-300"
                          aria-label="High priority announcement"
                        >
                          Priority
                        </span>
                      ) : null}
                      <p className="font-semibold text-[color:var(--color-ink-strong)]">
                        {viewAnnouncement.title}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-[color:var(--color-ink-subtle)]">
                      <span className="font-medium text-[color:var(--color-ink-muted)]">
                        {viewAnnouncement.createdByName ?? 'System'}
                      </span>
                      {' · '}
                      {formatDateTime(viewAnnouncement.createdAt)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setViewAnnouncement(null)}
                    aria-label="Close announcement"
                    className="text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer"
                  >
                    <X size={16} />
                  </button>
                </div>
                {viewAnnouncement.body ? (
                  <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                    <p className="text-sm text-[color:var(--color-ink-strong)] whitespace-pre-wrap">
                      {viewAnnouncement.body}
                    </p>
                  </div>
                ) : null}
                <div className="px-5 py-3 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] flex justify-end">
                  <button
                    type="button"
                    onClick={() => setViewAnnouncement(null)}
                    className="text-sm font-medium text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {/* Delete confirmation — same ConfirmDialog every other delete in
       *  the app goes through. Deleting is permanent for this user's
       *  feed (no undo), so a direct-mutate trash icon risked accidental
       *  data loss. Dialog stays open on error so the user can retry. */}
      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
        confirming={deleteMutation.isPending}
        title="Delete Notification?"
        confirmLabel="Delete"
        variant="danger"
        message={
          confirmDelete
            ? `"${confirmDelete.title}" will be permanently deleted from your notifications. This action can't be undone.`
            : ''
        }
      />

      {/* Bulk delete confirmation — same contract as the single-row
       *  dialog: nothing is deleted until confirmed here. */}
      <ConfirmDialog
        open={Boolean(bulkDeleteTarget)}
        onCancel={() => setBulkDeleteTarget(null)}
        onConfirm={() =>
          bulkDeleteTarget?.all
            ? deleteAllMutation.mutate()
            : bulkDeleteMutation.mutate(bulkDeleteTarget.ids)
        }
        confirming={bulkDeleteMutation.isPending || deleteAllMutation.isPending}
        title={`Delete ${bulkDeleteTarget?.count ?? 0} notification${
          (bulkDeleteTarget?.count ?? 0) === 1 ? '' : 's'
        }?`}
        confirmLabel="Delete"
        variant="danger"
        message={
          bulkDeleteTarget
            ? `${bulkDeleteTarget.count} selected notification${
                bulkDeleteTarget.count === 1 ? '' : 's'
              } will be permanently deleted from your notifications. This action can't be undone.`
            : ''
        }
      />

      {/* Pagination — the SHARED bar (same component as Members / Leads
       *  / Coupons) so the page gets the standard Rows-per-page selector
       *  the bespoke Previous/Next footer here lacked (QA-reported).
       *  Options capped at 100 to match the API's server-side limit
       *  clamp; only renders when there are rows. */}
      {rows.length > 0 ? (
        <div className="mt-4 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] overflow-hidden">
          <Pagination
            page={pagination.page}
            totalPages={Math.max(1, Math.ceil(pagination.total / limit))}
            total={pagination.total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={(n) => {
              setLimit(n)
              setPage(1)
            }}
            loading={listQuery.isFetching}
            limitOptions={NOTIFICATION_PAGE_SIZES}
          />
        </div>
      ) : null}
    </div>
  )
}

function TabButton({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'h-8 px-3 rounded-md text-sm font-medium cursor-pointer transition-colors ' +
        (active
          ? 'bg-[color:var(--color-accent-strong)] text-white'
          : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)]')
      }
    >
      {label}
    </button>
  )
}

function EmptyState({ tab, hasFilter }) {
  // Three subtly different empty states — "all caught up" (unread tab),
  // "filter knocked everything out" (filter active), and "nothing has
  // ever arrived here" (cold start). Same banner chrome across all
  // three for visual consistency with the rest of the app.
  if (tab === 'unread') {
    return (
      <EmptyStateBanner
        icon={CheckCheck}
        title="You're all caught up"
        body="New notifications will land here as they arrive — mentions, task updates, badge awards and admin events."
        tone="emerald"
      />
    )
  }
  if (hasFilter) {
    return (
      <EmptyStateBanner
        icon={Eye}
        title="No notifications match your filter"
        body="Clear the filter to see everything that's landed in your notification centre."
        tone="amber"
      />
    )
  }
  return (
    <EmptyStateBanner
      icon={Bell}
      title="No notifications yet"
      body="Mentions, task updates, badge awards and admin events will land here."
      tone="sky"
    />
  )
}

function NotificationRow({
  notification,
  selected,
  onToggleSelect,
  onClick,
  onDelete,
  onMarkRead,
  deleting,
}) {
  const isUnread = !notification.readAt
  const cfg = getTypeConfig(notification.kind)
  const TypeIcon = cfg.icon
  const senderName = notification.createdByName ?? null
  const senderInitials = initials(senderName)
  // Announcements are broadcast-shaped: even when a human posted them
  // (createdByName is set), the megaphone icon + amber gradient must win
  // so the row reads as a company-wide broadcast, not a person-to-person
  // ping — matching the notification bell.
  const isAnnouncement = notification.kind?.startsWith('announcement') ?? false

  // Stop row click from firing when the user clicks an action button
  // — otherwise deleting a row would also navigate to its link.
  function stop(e) {
    e.stopPropagation()
  }

  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={
        'flex items-start gap-3 px-5 py-4 cursor-pointer outline-none transition-colors focus-visible:bg-[color:var(--color-card-hover)] ' +
        (isUnread
          ? 'bg-[color:var(--color-accent-tint)] hover:bg-[color:var(--color-accent-bg)]'
          : 'hover:bg-[color:var(--color-card-hover)]')
      }
    >
      {/* Bulk-select checkbox — click is stopped so ticking a row never
       *  triggers the row's navigate/mark-read click. Same styling as
       *  the Files page's selection checkboxes. */}
      <input
        type="checkbox"
        checked={selected}
        onClick={stop}
        onChange={onToggleSelect}
        aria-label={`Select notification: ${notification.title}`}
        className="mt-2.5 h-4 w-4 shrink-0 cursor-pointer accent-[color:var(--color-accent)]"
      />
      {/* Avatar — announcements (megaphone) AND human-sender rows both use
       *  the SAME ocean→navy gradient (from-[#006CA5] to-[#02367B]) as the
       *  UserMenu pill / shared <Avatar> / bell, so an announcement's icon
       *  sits on the same blue background as a person's avatar. System
       *  events keep the type-tone treatment so they stay categorically
       *  distinct. */}
      <span
        className={
          'h-10 w-10 rounded-full flex items-center justify-center shrink-0 text-sm font-semibold border ' +
          (isAnnouncement || senderInitials
            ? 'bg-gradient-to-br from-[#006CA5] to-[#02367B] text-white border-transparent'
            : TONE_CLASS[cfg.tone] ?? TONE_CLASS.slate)
        }
        aria-hidden="true"
      >
        {isAnnouncement ? (
          <TypeIcon size={16} />
        ) : (
          senderInitials ?? <TypeIcon size={16} />
        )}
      </span>

      {/* Body — title, body line, meta strip. */}
      <div className="flex-1 min-w-0">
        <p
          className={
            'text-sm truncate ' +
            (isUnread
              ? 'font-semibold text-[color:var(--color-ink-strong)]'
              : 'text-[color:var(--color-ink-strong)]')
          }
        >
          {notification.title}
        </p>
        {notification.body ? (
          <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)] line-clamp-2">
            {notification.body}
          </p>
        ) : null}
        {/* Inline Accept / Decline buttons for invitation rows.
         *  Covers both Space and List invites — the action component
         *  parses the link to decide which endpoint to hit. Hidden
         *  for any other kind. The component itself stops click
         *  propagation so the row-level navigate-on-click doesn't
         *  fire while the mutation is in flight. */}
        {notification.kind === 'space.invited' ||
        notification.kind === 'list.invited' ? (
          <div className="mt-2">
            <SpaceInviteActions notification={notification} />
          </div>
        ) : null}
        <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs text-[color:var(--color-ink-subtle)]">
          <span className="font-medium text-[color:var(--color-ink-muted)]">
            {senderName ?? 'System'}
          </span>
          <span aria-hidden="true">·</span>
          <span>{formatDateTime(notification.createdAt)}</span>
          <span aria-hidden="true">·</span>
          <span
            className={
              'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ' +
              (TONE_CLASS[cfg.tone] ?? TONE_CLASS.slate)
            }
          >
            {cfg.label}
          </span>
        </div>
      </div>

      {/* Actions — Eye (mark read) on EVERY unread row. It was
       *  previously hidden on linked rows ("clicking the row already
       *  marks it read"), but that forces navigation: there was no way
       *  to mark one notification read and STAY on the page
       *  (QA-reported). The button stops propagation, so it never
       *  triggers the row's navigate-on-click. Trash always available. */}
      <div className="flex items-center gap-1 shrink-0">
        {isUnread ? (
          <button
            type="button"
            onClick={(e) => {
              stop(e)
              onMarkRead()
            }}
            title="Mark as read"
            aria-label="Mark as read"
            className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
          >
            <Eye size={14} />
          </button>
        ) : null}
        <button
          type="button"
          onClick={(e) => {
            stop(e)
            onDelete()
          }}
          disabled={deleting}
          title="Delete notification"
          aria-label="Delete notification"
          className="p-1.5 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50"
        >
          <Trash2 size={14} />
        </button>
        {isUnread ? (
          <span
            className="ml-1 h-2 w-2 rounded-full bg-[color:var(--color-accent)] shrink-0"
            aria-label="Unread"
          />
        ) : (
          <span className="ml-1 h-2 w-2 shrink-0" aria-hidden="true" />
        )}
      </div>
    </li>
  )
}
